import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import {
  useCreateProjectMutation,
  useGetProjectBySlugQuery,
} from '../api/indexerApi';
import { getIndexerErrorInfo } from '../api/errors';
import type { Project } from '../api/types';
import { projectApiRef } from './project-api-ref';
import { siteUrlFromDomainName } from './panel-user';

interface ProjectContextValue {
  projects: Project[];
  project: Project | null;
  /** slug دامنه یا id — برای مسیرهای `/projects/:projectId/...` */
  projectRef: string | null;
  loading: boolean;
  domainSlug: string | null;
  domainName: string | null;
  setProjectId: (id: string) => void;
  refresh: () => Promise<void>;
  create: (input: {
    name: string;
    slug: string;
    siteUrl?: string;
    sitemapUrl?: string;
    indexNowDomain?: string;
  }) => Promise<Project>;
  createForCurrentDomain: () => Promise<Project>;
  applyProject: (updated: Project) => void;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const selectedDomain = useSelector(
    (state: {
      selectedDomain?: { slug?: string; name?: string; show_name?: string };
    }) => state.selectedDomain,
  );
  const domainSlug =
    selectedDomain?.slug && selectedDomain.slug !== 'no-domain'
      ? selectedDomain.slug
      : null;
  const domainName =
    selectedDomain?.name && selectedDomain.name !== 'no-domain'
      ? selectedDomain.name
      : null;

  const {
    data: fetchedProject,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetProjectBySlugQuery(domainSlug ?? '', {
    skip: !domainSlug,
  });

  const notFound = isError && getIndexerErrorInfo(error).code === 404;
  const [projectOverride, setProjectOverride] = useState<Project | null>(null);

  useEffect(() => {
    setProjectOverride(null);
  }, [domainSlug]);

  const project =
    projectOverride ??
    (domainSlug && !notFound && !isError ? (fetchedProject ?? null) : null);

  const projectRef = useMemo(
    () => (project || domainSlug ? projectApiRef(project, domainSlug) : null),
    [project, domainSlug],
  );

  const [createProjectMut] = useCreateProjectMutation();

  const refresh = useCallback(async () => {
    if (!domainSlug) return;
    await refetch().unwrap();
  }, [domainSlug, refetch]);

  const applyProject = useCallback((updated: Project) => {
    setProjectOverride(updated);
  }, []);

  const create = useCallback(
    async (input: {
      name: string;
      slug: string;
      siteUrl?: string;
      sitemapUrl?: string;
      indexNowDomain?: string;
    }) => {
      const created = await createProjectMut(input).unwrap();
      applyProject(created);
      return created;
    },
    [applyProject, createProjectMut],
  );

  const createForCurrentDomain = useCallback(async () => {
    if (!domainSlug || !domainName) {
      throw new Error('دامنه‌ای انتخاب نشده است');
    }
    const siteUrl = siteUrlFromDomainName(domainName);
    return create({
      name: domainName,
      slug: domainSlug,
      siteUrl,
      indexNowDomain: siteUrl?.replace(/\/$/, ''),
    });
  }, [create, domainName, domainSlug]);

  const value = useMemo(
    () => ({
      projects: project ? [project] : [],
      project,
      projectRef,
      loading: Boolean(domainSlug) && isFetching && !projectOverride,
      domainSlug,
      domainName,
      setProjectId: (_id: string) => {
        /* سلکت با DomainSelector / slug انجام می‌شود */
      },
      refresh,
      create,
      createForCurrentDomain,
      applyProject,
    }),
    [
      project,
      projectRef,
      domainSlug,
      domainName,
      isFetching,
      projectOverride,
      refresh,
      create,
      createForCurrentDomain,
      applyProject,
    ],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error('useProject must be used inside ProjectProvider');
  }
  return ctx;
}
